import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { IAgentMemoryDTO } from '../../../../../api/events.dao';
import { LONG_RICH_TEXT_LENGTH } from '../../../../../constants/common.constants';
import { MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE } from '../../../../../constants/validation-errors';
import { getReportDateTimeString } from '../../../../../infrastructure/utils/date.util';
import type { IAgentMemoryFormContainerProps } from './agent-memory-form.container';
import { AGENT_MEMORY_LAST_MODIFIED_TEXT } from './agent-memory-form.text';

export default class AgentMemoryFormVM {
    @mobx.observable
    public isSaving = false;
    @mobx.observable
    public loadFailed = false;
    @mobx.observable
    public agentMemory = '';
    @mobx.observable
    public lastModifiedBy: string | null = null;
    @mobx.observable
    public lastModifiedAt: string | null = null;
    private intl: IntlShape;
    private fetchAgentMemory: () => Promise<IAgentMemoryDTO | null>;
    private saveAgentMemory: (agentMemory: string) => Promise<IAgentMemoryDTO | null>;

    @mobx.action
    public initialize(formProps: IAgentMemoryFormContainerProps) {
        this.intl = formProps.intl;
        this.fetchAgentMemory = formProps.fetchAgentMemory;
        this.saveAgentMemory = formProps.saveAgentMemory;
        this.isSaving = false;
        this.loadFailed = false;
        this.agentMemory = '';
        this.lastModifiedBy = null;
        this.lastModifiedAt = null;
    }

    public async load(): Promise<void> {
        const response = await this.fetchAgentMemory();
        mobx.runInAction(() => {
            // saving after a failed load would replace a stored memory the user never saw
            this.loadFailed = !response;
        });
        this.applyResponse(response);
    }

    @mobx.action
    public setAgentMemory = (value: string): void => {
        this.agentMemory = value;
    };

    @mobx.computed
    public get agentMemoryLengthError(): string | null {
        return this.agentMemory.length > LONG_RICH_TEXT_LENGTH ? MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE : null;
    }

    @mobx.computed
    public get lastModifiedLabel(): string | null {
        const modifiedAt = this.lastModifiedAt ? new Date(this.lastModifiedAt) : null;
        if (!this.lastModifiedBy || !modifiedAt || isNaN(modifiedAt.getTime())) {
            return null;
        }
        return this.intl.formatMessage(AGENT_MEMORY_LAST_MODIFIED_TEXT, {
            user: this.lastModifiedBy,
            dateTime: getReportDateTimeString(modifiedAt),
        });
    }

    /**
     * Resolves to true (closing the sub-window) only when the memory was saved.
     */
    public formSubmitAction = async (): Promise<boolean> => {
        if (this.loadFailed || this.agentMemoryLengthError) {
            return false;
        }
        mobx.runInAction(() => {
            this.isSaving = true;
        });
        try {
            const response = await this.saveAgentMemory(this.agentMemory);
            this.applyResponse(response);
            return !!response;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    @mobx.action
    private applyResponse(response: IAgentMemoryDTO | null): void {
        if (response) {
            this.agentMemory = response.agentMemory ?? '';
            this.lastModifiedBy = response.lastModifiedBy;
            this.lastModifiedAt = response.lastModifiedAt;
        }
    }
}
